/* eslint-disable no-unused-vars */
import React, { useState, useRef } from 'react';

const COS_TARGETS = [
  { id: 'dev_advances',    label: 'Dev Advances' },
  { id: 'marketing_rec',  label: 'Marketing (Recoupable)' },
  { id: 'marketing_nonrec', label: 'Marketing (Non-Recoupable)' },
  { id: 'publishing_rec', label: 'Publishing (Recoupable)' },
  { id: 'royalties',      label: 'Royalties Payable' },
  { id: 'other_cos',      label: 'Other COS' },
];

const REVENUE_TARGETS = [
  { id: 'rev_platform',    label: 'Net Revenue from Platform' },
  { id: 'rev_royalties',   label: 'Net Royalties from Publisher' },
  { id: 'rev_dev',         label: 'Development Revenue' },
  { id: 'rev_other',       label: 'Other Income' },
];

function MappingRow({ account, mapping, onSave, ohCategories }) {
  const [type, setType]       = useState(mapping?.type || '');
  const [targetId, setTargetId] = useState(mapping?.targetId || '');
  const [saved, setSaved]     = useState(!!mapping?.type);

  const handleSave = () => {
    const label = getLabel(type, targetId);
    onSave(account.account_name, type, targetId, label);
    setSaved(true);
  };

  const getLabel = (t, tid) => {
    if (t === 'revenue') return REVENUE_TARGETS.find(x => x.id === tid)?.label || tid;
    if (t === 'cos') return COS_TARGETS.find(x => x.id === tid)?.label || tid;
    if (t === 'overhead_sub') {
      for (const cat of ohCategories || []) {
        const sub = cat.subs?.find(s => s.id === tid);
        if (sub) return `${cat.name} / ${sub.name}`;
      }
    }
    return '';
  };

  const isDirty = type !== (mapping?.type || '') || targetId !== (mapping?.targetId || '');

  return (
    <tr style={{ borderBottom: '1px solid var(--e0)' }}>
      <td style={{ padding: '5px 10px', fontSize: 11, color: 'var(--t1)' }}>{account.account_name}</td>
      <td style={{ padding: '5px 8px', fontSize: 10, color: 'var(--t3)' }}>{account.section}</td>
      <td style={{ padding: '5px 8px' }}>
        <select value={type} onChange={e => { setType(e.target.value); setTargetId(''); setSaved(false); }}
          style={{ fontSize: 11, padding: '3px 20px 3px 6px', width: '100%' }}>
          <option value="">— unmapped —</option>
          <option value="revenue">Revenue</option>
          <option value="cos">Cost of Sales</option>
          <option value="overhead_sub">Overhead Sub-category</option>
          <option value="below_ebitda_sub">Below EBITDA Item</option>
          <option value="salaries">Salaries</option>
          <option value="contractors">Contractors</option>
          <option value="other_staff">Other Staff Costs</option>
          <option value="ignore">Ignore</option>
        </select>
      </td>
      <td style={{ padding: '5px 8px' }}>
        {type === 'revenue' && (
          <select value={targetId} onChange={e => { setTargetId(e.target.value); setSaved(false); }}
            style={{ fontSize: 11, padding: '3px 20px 3px 6px', width: '100%' }}>
            <option value="">— select —</option>
            {REVENUE_TARGETS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        )}
        {type === 'cos' && (
          <select value={targetId} onChange={e => { setTargetId(e.target.value); setSaved(false); }}
            style={{ fontSize: 11, padding: '3px 20px 3px 6px', width: '100%' }}>
            <option value="">— select —</option>
            {COS_TARGETS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        )}
        {type === 'overhead_sub' && (
          <select value={targetId} onChange={e => { setTargetId(e.target.value); setSaved(false); }}
            style={{ fontSize: 11, padding: '3px 20px 3px 6px', width: '100%' }}>
            <option value="">— select category —</option>
            {(ohCategories || []).map(cat => (
              <optgroup key={cat.id} label={cat.name}>
                {(cat.subs || []).map(sub => (
                  <option key={sub.id} value={sub.id}>{sub.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
        )}
        {type === 'below_ebitda_sub' && (
          <select value={targetId} onChange={e => { setTargetId(e.target.value); setSaved(false); }}
            style={{ fontSize: 11, padding: '3px 20px 3px 6px', width: '100%' }}>
            <option value="">— select —</option>
            {(ohCategories || []).filter(c => c.belowEbitda).map(cat => (
              <optgroup key={cat.id} label={cat.name}>
                {(cat.subs || []).map(sub => (
                  <option key={sub.id} value={sub.id}>{sub.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
        )}
        {['salaries','contractors','other_staff'].includes(type) && (
          <span style={{ fontSize: 10, color: 'var(--t3)', fontStyle: 'italic' }}>Maps to People costs</span>
        )}
        {type === 'ignore' && <span style={{ fontSize: 10, color: 'var(--t4)', fontStyle: 'italic' }}>Will not be imported</span>}
      </td>
      <td style={{ padding: '5px 8px', textAlign: 'center' }}>
        {isDirty ? (
          <button onClick={handleSave}
            style={{ fontSize: 9, padding: '3px 10px', background: 'var(--blue-dim)', border: '1px solid var(--blue)', color: 'var(--blue)', borderRadius: 'var(--r)', cursor: 'pointer' }}>
            Save
          </button>
        ) : saved ? (
          <span style={{ fontSize: 10, color: 'var(--green)' }}>✓</span>
        ) : null}
      </td>
    </tr>
  );
}

export default function PLUploadPanel({
  uploads, accounts, mappings, snapshots, loading,
  uploadPL, saveMapping, restoreSnapshot,
  ohCategories, ohValues,
  onRestoreOhValues,
}) {
  const [uploading, setUploading]   = useState(false);
  const [uploadMsg, setUploadMsg]   = useState('');
  const [restoring, setRestoring]   = useState(false);
  const [filterSection, setFilter]  = useState('all');
  const [filterMapped, setFilterM]  = useState('all');
  const fileRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadMsg('Parsing file…');
    try {
      // Load SheetJS
      if (!window.XLSX) {
        await new Promise((res, rej) => {
          const s = document.createElement('script');
          s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
          s.onload = res; s.onerror = rej;
          document.head.appendChild(s);
        });
      }
      const buf = await file.arrayBuffer();
      setUploadMsg('Uploading and storing data…');
      const result = await uploadPL(buf, file.name, ohValues, ohCategories);
      setUploadMsg(`✓ Uploaded ${file.name} — ${result.accounts.length} nominal accounts found across ${result.months.length} months`);
    } catch (err) {
      setUploadMsg(`⚠ Error: ${err.message}`);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleRestore = async (id, label) => {
    if (!window.confirm(`Restore overhead values to state before "${label}"? This will overwrite current overhead data.`)) return;
    setRestoring(true);
    const vals = await restoreSnapshot(id);
    if (vals) { onRestoreOhValues(vals); alert('Restored successfully.'); }
    else alert('Snapshot not found.');
    setRestoring(false);
  };

  const sections = ['all', 'Turnover', 'Cost of Sales', 'Administrative Costs'];
  const filtered = accounts.filter(a => {
    if (filterSection !== 'all' && a.section !== filterSection) return false;
    if (filterMapped === 'mapped'   && !mappings[a.account_name]?.type) return false;
    if (filterMapped === 'unmapped' && mappings[a.account_name]?.type) return false;
    return true;
  });

  const mappedCount   = accounts.filter(a => mappings[a.account_name]?.type && mappings[a.account_name]?.type !== 'ignore').length;
  const ignoredCount  = accounts.filter(a => mappings[a.account_name]?.type === 'ignore').length;
  const unmappedCount = accounts.length - mappedCount - ignoredCount;

  return (
    <div>
      {/* Upload section */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head">
          <span className="card-title">Upload Xero P&L</span>
          <span className="card-meta">Excel export · Months as columns</span>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <input ref={fileRef} type="file" accept=".xlsx,.xls"
              onChange={handleFile} disabled={uploading}
              style={{ fontSize: 12, color: 'var(--t2)' }} />
            {uploading && <span style={{ fontSize: 11, color: 'var(--t3)' }}>Uploading…</span>}
          </div>
          {uploadMsg && (
            <div style={{ fontSize: 11, color: uploadMsg.startsWith('⚠') ? 'var(--red)' : 'var(--green)', padding: '6px 10px', background: 'var(--s1)', borderRadius: 'var(--r)', border: '1px solid var(--e0)' }}>
              {uploadMsg}
            </div>
          )}
          <div style={{ fontSize: 10, color: 'var(--t4)', marginTop: 8 }}>
            Upload a Xero P&L Excel export. Nominal accounts will be extracted and stored. Map them to model categories below. Actuals for months at or before the current month-end will overwrite forecast inputs.
          </div>
        </div>
      </div>

      {/* Upload history */}
      {uploads.length > 0 && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-head"><span className="card-title">Upload History</span></div>
          <div className="card-body" style={{ padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--s1)' }}>
                  {['File','Uploaded','Months Covered'].map(h => (
                    <th key={h} style={{ padding: '6px 10px', fontSize: 9, color: 'var(--t4)', textAlign: 'left', fontWeight: 400, letterSpacing: '.08em', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {uploads.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid var(--e0)' }}>
                    <td style={{ padding: '5px 10px', fontSize: 12, color: 'var(--t1)' }}>{u.filename}</td>
                    <td style={{ padding: '5px 10px', fontSize: 11, color: 'var(--t3)' }}>{new Date(u.uploaded_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                    <td style={{ padding: '5px 10px', fontSize: 11, color: 'var(--t3)' }}>{(u.months_covered || []).length} months · {u.months_covered?.[u.months_covered.length - 1]} to {u.months_covered?.[0]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Snapshots / restore */}
      {snapshots.length > 0 && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-head">
            <span className="card-title">Restore Points</span>
            <span className="card-meta">Snapshots taken before each upload · Max 10</span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--s1)' }}>
                  {['Snapshot','Date','Months Affected',''].map(h => (
                    <th key={h} style={{ padding: '6px 10px', fontSize: 9, color: 'var(--t4)', textAlign: 'left', fontWeight: 400, letterSpacing: '.08em', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {snapshots.map(s => (
                  <tr key={s.id} style={{ borderBottom: '1px solid var(--e0)' }}>
                    <td style={{ padding: '5px 10px', fontSize: 12, color: 'var(--t1)' }}>{s.label}</td>
                    <td style={{ padding: '5px 10px', fontSize: 11, color: 'var(--t3)' }}>{new Date(s.snapshot_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    <td style={{ padding: '5px 10px', fontSize: 11, color: 'var(--t3)' }}>{(s.months_affected || []).length} months</td>
                    <td style={{ padding: '5px 10px' }}>
                      <button onClick={() => handleRestore(s.id, s.label)} disabled={restoring}
                        style={{ fontSize: 9, padding: '3px 10px', background: 'transparent', border: '1px solid var(--e1)', color: 'var(--t3)', borderRadius: 'var(--r)', cursor: 'pointer' }}>
                        Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Nominal account mapping */}
      {accounts.length > 0 && (
        <div className="card">
          <div className="card-head">
            <span className="card-title">Nominal Account Mapping</span>
            <span className="card-meta">
              {mappedCount} mapped · {ignoredCount} ignored · {unmappedCount} unmapped
            </span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {/* Filters */}
            <div style={{ display: 'flex', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--e0)', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 9, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Section</span>
                <select value={filterSection} onChange={e => setFilter(e.target.value)} style={{ fontSize: 11 }}>
                  {sections.map(s => <option key={s} value={s}>{s === 'all' ? 'All Sections' : s}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 9, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Status</span>
                <select value={filterMapped} onChange={e => setFilterM(e.target.value)} style={{ fontSize: 11 }}>
                  <option value="all">All</option>
                  <option value="unmapped">Unmapped only</option>
                  <option value="mapped">Mapped only</option>
                </select>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--s1)' }}>
                    {['Nominal Account','Section','Maps To','Target',''].map(h => (
                      <th key={h} style={{ padding: '6px 10px', fontSize: 9, color: 'var(--t4)', textAlign: 'left', fontWeight: 400, letterSpacing: '.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(acc => (
                    <MappingRow key={acc.account_name} account={acc}
                      mapping={mappings[acc.account_name]}
                      onSave={saveMapping}
                      ohCategories={ohCategories} />
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={5} className="nomsg">No accounts match the current filter.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {accounts.length === 0 && !loading && (
        <div className="nomsg">No nominal accounts yet — upload a P&L to get started.</div>
      )}
    </div>
  );
}
